// ======================================================
// STATE & ROOM MANAGEMENT
// ======================================================
class TourState {
  static ROOM_BLOCK_MAX = 2000;      // current-room block in the instructions
  static ROOM_LIST_FULL_MAX = 3000;  // room list keeps full descriptions up to this
  static ROOM_SUMMARY_MAX = 120;     // …otherwise each room gets one clipped line

  constructor(tourData = {}, currentScene = null) {
    this.tourData = tourData;
    this.currentScene = currentScene;
    this.language = 'en';
    // False only while the very first session config is being built. Later
    // session.update payloads (scene change, RAG refresh) must not re-issue the
    // greeting instruction, or the guide says "welcome" mid-conversation.
    this.sessionStarted = false;
  }

  markSessionStarted() {
    this.sessionStarted = true;
  }

  // ── Current visitor turn ─────────────────────────────────────────
  // Transcript of what the visitor said in the turn being answered; reset when
  // a new turn starts so a tool call never reads the previous turn's words.

  startUserTurn() {
    this.userTranscript = null;
  }

  setUserTranscript(text) {
    this.userTranscript = text || null;
  }

  // ── Question to answer once a room has loaded ────────────────────
  // ("what's the flooring like in the bedroom, and take me there")

  static ARRIVAL_QUESTION_TTL_MS = 20000;

  setArrivalQuestion(question, roomId) {
    this.arrivalQuestion = { question, roomId: String(roomId), at: Date.now() };
  }

  // Held while the guide asks "Bedroom 1 or Bedroom 2?", so the question
  // survives the visitor's one-word answer.
  holdQuestion(question) {
    this.heldQuestion = question ? { question, at: Date.now() } : null;
  }

  takeHeldQuestion() {
    const q = this.heldQuestion;
    this.heldQuestion = null;
    if (!q || Date.now() - q.at > TourState.ARRIVAL_QUESTION_TTL_MS * 3) return null;
    return q.question;
  }

  hasArrivalQuestion() {
    return !!this.arrivalQuestion;
  }

  // The question for the room that just loaded — once. A different room
  // (the visitor clicked elsewhere meanwhile) or a stale one drops it.
  takeArrivalQuestion(roomId) {
    const q = this.arrivalQuestion;
    this.arrivalQuestion = null;
    if (!q) return null;
    if (String(roomId) !== q.roomId) return null;
    if (Date.now() - q.at > TourState.ARRIVAL_QUESTION_TTL_MS) return null;
    return q.question;
  }

  setTourData(tourData = {}) {
    this.tourData = tourData;
  }

  setCurrentScene(sceneId) {
    this.currentScene = sceneId;
  }

  getRoomInfo(roomId = this.currentScene) {
    // The model passes room_id as a string ("19294"); scene ids may be numbers.
    if (roomId === null || roomId === undefined) return undefined;
    return (this.tourData.scenes || []).find(scene => String(scene.id) === String(roomId));
  }

  /**
   * Room block for the session instructions: a summary list of every room plus
   * the CURRENT room in full (description, arrival message, visible objects,
   * hotspots). Rebuilt on every scene change via sendSessionUpdate, so the
   * guide can answer questions about the room it is in without a knowledge
   * base lookup. The current-room block is capped at ROOM_BLOCK_MAX chars.
   */
  buildRoomContext(currentScene) {
    if (!this.tourData?.scenes) return '';
    const scenes = Object.values(this.tourData.scenes);
    const nameOf = (s) => s?.title || s?.name || s?.id;
    const str = (v) => (typeof v === 'string' ? v.trim() : '');
    const clip = (t, n) => (t.length > n ? `${t.slice(0, n - 1)}…` : t);
    const oneLine = (t) => str(t).replace(/\s+/g, ' ');

    // Full per-room descriptions while they fit; otherwise one line each.
    const fullLines = scenes.map(s =>
      `-"${nameOf(s)}" (R_ID: ${s.id})${str(s.description) ? ' desc: ' + oneLine(s.description) : ''}`);
    const fullLen = fullLines.reduce((n, l) => n + l.length + 1, 0);
    const rooms = (fullLen <= TourState.ROOM_LIST_FULL_MAX
      ? fullLines
      : scenes.map(s => `-"${nameOf(s)}" (R_ID: ${s.id})${str(s.description) ? ' desc: ' + clip(oneLine(s.description), TourState.ROOM_SUMMARY_MAX) : ''}`)
    ).join('\n');

    const currentRoom = this.getRoomInfo(currentScene);
    if (!currentRoom) return `\nAVAILABLE ROOMS:\n${rooms}\n`;

    // Structural parts are never cut — IDs and the describe coordinates drive
    // on_open_info / rotate_view. Only free text (hotspot notes, room details,
    // arrival message) is trimmed to keep the block near ROOM_BLOCK_MAX.
    const header = `CURRENT LOCATION: "${nameOf(currentRoom)}" (R_ID: ${currentRoom.id})\n`;
    const describe = str(currentRoom.describe) ? `describe: ${oneLine(currentRoom.describe)}\n` : '';
    let hotspots = '';
    if (currentRoom.hotspots?.length) {
      let noteBudget = 600;
      hotspots = 'HOTSPOTS:\n' + currentRoom.hotspots.map(h => {
        let note = oneLine(h.systemPrompt);
        if (note) {
          note = noteBudget > 40 ? clip(note, Math.min(200, noteBudget)) : '';
          noteBudget -= note.length;
        }
        return `-"${h.title || h.hotspotID}" (H_ID: ${h.hotspotID})${note ? ': ' + note : ''}`;
      }).join('\n') + '\n';
    }
    let budget = TourState.ROOM_BLOCK_MAX - header.length - describe.length - hotspots.length;
    let details = '';
    const desc = oneLine(currentRoom.description);
    const msg = oneLine(currentRoom.message);
    if (desc && budget > 50) {
      const line = `details: ${clip(desc, budget - 10)}\n`;
      details += line;
      budget -= line.length;
    }
    if (msg && msg !== desc && budget > 50) {
      details += `arrival message: ${clip(msg, budget - 18)}\n`;
    }
    const current = header + details + describe + hotspots;
    return `
AVAILABLE ROOMS:
${rooms}
${current}
`;
  }

  findBestRoomMatch(query) {
    const normalizedQuery = String(query || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!normalizedQuery) return null;
    // scenes is an array: iterate the scenes and use THEIR id. Object.entries()
    // gave the array index ("0", "1", …) as the id, so "19294" matched index "1"
    // and navigation went to the wrong room under a bogus id.
    for (const scene of Object.values(this.tourData.scenes || {})) {
      const id = String(scene.id);
      const normalizedId = id.toLowerCase().replace(/[^a-z0-9]/g, '');
      const normalizedTitle = (scene.title || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      if (
        normalizedId.includes(normalizedQuery) || normalizedQuery.includes(normalizedId) ||
        normalizedTitle.includes(normalizedQuery) || normalizedQuery.includes(normalizedTitle)
      ) {
        return { id, title: scene.title || id };
      }
    }
    return null;
  }
}

export default TourState;